import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ApiClient } from '../services/apiClient';
import { logger } from '../logger';

const sanitize = (s) =>
  (s || '')
    .replace(/^[\s*_`"'~-]+/, '')
    .replace(/[\s*_`"'~-]+$/, '')
    .trim();

const formatRelative = (isoTs) => {
  if (!isoTs) return '';
  const t = Date.parse(isoTs);
  if (isNaN(t)) return '';
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w} Week${w === 1 ? '' : 's'} ago`;
  const mo = Math.floor(d / 30);
  return `${mo} Month${mo === 1 ? '' : 's'} ago`;
};

const SearchModal = ({ onClose, project, userEmail, onSelectConversation }) => {
  const [dataSource, setDataSource] = useState('DCLI Benefits');
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let active = true;
    async function load() {
      if (!project || !userEmail) return;
      setLoading(true);
      setLoadError(null);
      try {
        const normProject = typeof project === 'object'
          ? (project.id || project.project || project.code || project.name || project)
          : project;
        const res = await ApiClient.getConversations({
          project: normProject,
          user: userEmail.toLowerCase()
        });
        const list = Array.isArray(res?.conversations) ? res.conversations : [];
        if (active) setConversations(list);
      } catch (e) {
        if (active) setLoadError('Failed to load conversations');
        logger.error('[SearchModal] load conversations error:', e);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [project, userEmail]);

  useEffect(() => {
    // lock scroll & mark page state
    document.body.classList.add('search-locked', 'search-dim');
    return () => {
      document.body.classList.remove('search-locked', 'search-dim');
    };
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(c =>
      (c.title || '').toLowerCase().includes(q)
    );
  }, [query, conversations]);

  return createPortal(
    <div className="search-modal-overlay" onClick={handleOverlayClick}>
      <div className="search-modal">
        <div className="search-modal-header">
          <h2 className="search-modal-title">Search Conversation</h2>
          <button className="search-modal-close" onClick={onClose} aria-label="Close search conversation">
            ✕
          </button>
        </div>
        
        <div className="search-modal-content">
          <div className="search-box">
            <div className="search-form">
              <div className="search-form-section">
                <label className="search-form-label">Provide Conversation details</label>
                <select 
                  className="search-form-select"
                  value={dataSource}
                  onChange={(e) => setDataSource(e.target.value)}
                >
                  <option value="DCLI Benefits - Database">
                    DCLI Benefits
                  </option>
                </select>
              </div>
              
              <div className="search-form-section">
                <label className="search-form-label">Search here</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    className="search-form-input"
                    placeholder="Search your conversations..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
              </div>
              
              <button className="search-conversation-btn">
                Search Conversation
              </button>
            </div>
          </div>
          
          <div className="search-box">
            {loading && <div className="search-modal-status">Loading...</div>}
            {loadError && <div className="search-modal-error">{loadError}</div>}
            {!loading && !loadError && filtered.length === 0 && (
              <div className="search-modal-empty">No matching conversations.</div>
            )}
            <div className="recent-conversations">
              <h3 className="recent-conversations-title">Recent Conversations</h3>
              <ul className="search-modal-results" style={{ listStyle:'none', margin:0, padding:0, display:'flex', flexDirection:'column', gap:12 }}>
                {filtered.map(c => {
                  const rel = (c.relevance !== undefined && c.relevance !== null)
                    ? `${c.relevance}% Relevance`
                    : null;
                  const author = c.userid || c.user || c.owner || userEmail || 'You';
                  const time = formatRelative(c.timestamp);
                  const metaParts = [author, rel, time].filter(Boolean);
                  return (
                    <li
                      key={c.id}
                      className="search-modal-result"
                      onClick={() => {
                        onSelectConversation?.(c.fetchId || c.id);
                        onClose?.();
                      }}
                      style={{
                        border:'1px solid #eee',
                        borderRadius:12,
                        padding:'10px 14px 11px',
                        background:'#fff',
                        cursor:'pointer',
                        transition:'background .15s,border-color .15s',
                        display:'flex',
                        flexDirection:'column',
                        gap:4
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#fafafa'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                    >
                      <div
                        className="search-modal-title"
                        style={{
                          fontSize:14,
                          fontWeight:600,
                          color:'#c4322b',
                          lineHeight:1.25,
                          wordBreak:'break-word'
                        }}
                        title={sanitize(c.title)}
                      >
                        {sanitize(c.title)}
                      </div>
                      {metaParts.length > 0 && (
                        <div
                          className="search-modal-meta"
                          style={{
                            fontSize:12,
                            color:'#555',
                            display:'flex',
                            flexWrap:'wrap',
                            gap:4,
                            lineHeight:1.2
                          }}
                        >
                          <span>{metaParts.join(' • ')}</span>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SearchModal;
